// theme-control.ts — the console's appearance MODEL helpers: which themes a
// mode offers under the painted style, the theme swatch, the style hint and
// R70's paint-only preview lane (no UI surface reaches it since #3242 — see
// previewTheme).
//
// HISTORY. This file used to render the home screen's appearance POPOVER — a
// sun/moon mode button, a gear, and a menu with the Dark/Light segment, the
// per-mode color-scheme dropdown, the style dropdown and the API-tokens link. The
// owner ruling of 2026-09-08 moved that menu INTO the avatar menu on every
// surface (pi-stack/src/client/avatar-menu.ts, mounted here by
// account-menu.ts) and RETIRED the gear and the sun on the Bridge. What stays
// is what the shared menu asks a surface for and what the headless suite
// drills: the roster, the preview lane, and the two row-markup helpers.
//
// R70 — hover previews the theme INSTANTLY, without committing. Affordable
// only since design-system-foundation converted gridThemeParams() to var()
// strings resolved at paint time: a theme flip is now a CSS-variable write,
// so previewing on every hover costs a repaint, not three grid rebuilds.
// The preview lane below is deliberately NOT apply() — see previewTheme().

import {
  currentSeat, themeFor, themeTokenVars, MANAGED_VARS, paintedStyleId,
  type AppearanceSeat,
} from './appearance.js';
import { THEMES, type AppTheme } from './generated/themes.js';
import { type AppStyle } from './generated/styles.js';
import { groundStyle } from './style-substrate.js';
import { themeSwatchHtml, styleHintHtml as sharedStyleHintHtml } from 'pi-stack/client/avatar-menu.js';

/** Themes offered for a mode, WITHIN THE PAINTED STYLE. The registry holds
 * every mode's and every style's themes in one list; a light theme selected in
 * dark mode, or a Glass palette selected under Mainframe, would render as that
 * combination's default (resolveTheme's contract) — i.e. the picker would
 * appear to do nothing. Filtering on BOTH axes is what keeps every option in
 * the list truthful.
 *
 * The style filter is the owner correction of 2026-09-07: without it the
 * console offered all 46 themes under every style, including 25 that belong to
 * OTHER styles and would each have normalized straight back to the painted
 * style's default. The Helm's own picker has always filtered
 * (`themesForMode(mode, currentStyle())`, app.ts). */
export function themesForMode(mode: 'dark' | 'light', styleId?: string): AppTheme[] {
  const style = styleId ?? paintedStyleId(currentSeat());
  return THEMES.filter((t) => t.mode === mode && (t.styleId ?? 'blueprint') === style);
}

/** A three-dot swatch of a theme's ground/surface/accent — the shared menu's
 * row markup, re-exported so the console keeps ONE import site. */
export function swatchHtml(t: AppTheme): string {
  return themeSwatchHtml(t);
}

/** A one-word material hint beside a style's name in the picker. */
export function styleHintHtml(s: AppStyle): string {
  return sharedStyleHintHtml(s);
}

// ----------------------------------------------------------------------
// R70's preview lane — vars + data-theme ONLY, never apply().
//
// apply() (appearance.ts) has three side effects a hover must not have,
// each a measured hazard:
//   1. it writes the localStorage pre-paint cache ('hc-theme'/'hc-theme-
//      vars'), which public/index.html replays — a hover would poison the
//      next hard refresh's FIRST PAINT with a theme the user never chose;
//   2. it fires the onAppearanceChange fan-out — value-consumers react to
//      every mouse-move (the grid itself no longer needs the fan-out, but
//      the listener seam is not the preview's to spend);
//   3. it mutates the module-level seat that startAppearanceSync's 12s poll
//      compares against — the poll would read "seat changed", and apply the
//      SERVER seat over the preview mid-hover.
// So the preview paints through this parallel lane: the same MANAGED_VARS
// write-or-REMOVE walk apply() does (a var the previewed theme does not
// define is removed, not left behind — the leak the MANAGED_VARS invariant
// exists to catch), and nothing else. endPreview() repaints from
// currentSeat() — the COMMITTED seat, never the previously hovered one.
// ----------------------------------------------------------------------

/** Stamp mode + theme vars on <html>. The shared half of apply()'s step 1+2,
 * with none of its persistence or fan-out. */
function paintSeat(seat: AppearanceSeat): void {
  const root = document.documentElement;
  if (seat.theme === 'light') root.setAttribute('data-theme', 'light');
  else root.removeAttribute('data-theme');
  // Stamp the style too (owner ruling 2026-09-06): a preview must paint the
  // SAME two axes apply() does, so hovering a theme keeps the chosen material
  // and hovering a style previews the material live. Seat-gated, exactly as
  // apply() is.
  root.setAttribute('data-style', paintedStyleId(seat));
  // …and the GROUND with it (owner correction 2026-09-07). A preview that
  // stamped the attribute but left the previous style's background layer in
  // place would show one style's materials over another style's ground —
  // the half-state, one layer down. Same call apply() makes.
  groundStyle(paintedStyleId(seat));
  const vars = themeTokenVars(themeFor(seat));
  for (const name of MANAGED_VARS) {
    const v = vars[name];
    if (v !== undefined) root.style.setProperty(name, v);
    else root.style.removeProperty(name);
  }
}

let previewActive = false;

/** Preview a seat: paint it, commit nothing. Exported for the test harness
 * (home-chrome.test.mjs drills the lane's invariants). NO UI surface reaches
 * it since #3242 (owner 2026-09-14: pickers apply on SELECT only, never on
 * hover) — account-menu.ts no longer hands it to the avatar menu. */
export function previewTheme(seat: AppearanceSeat): void {
  previewActive = true;
  paintSeat(seat);
}

/** Revert to the COMMITTED seat. Idempotent, and a no-op when nothing is
 * being previewed — so every dismiss path can call it unconditionally. */
export function endPreview(): void {
  if (!previewActive) return;
  previewActive = false;
  paintSeat(currentSeat());
}
